/**
 * PageHeader - Shared Component for All ML Infrastructure Pages
 * 
 * Provides consistent header across all 9 ML pages with title, subtitle,
 * refresh button, and optional custom actions.
 * 
 * Following BRAIN-PRE-CODE-CHECKLIST.md:
 * - Clean single imports
 * - Defensive null checks
 * - No hardcoded values
 * - Proper TypeScript types
 */

import React from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  onRefresh?: () => void;
  loading?: boolean;
  actions?: React.ReactNode;
  lastUpdated?: Date | null;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  onRefresh,
  loading = false,
  actions,
  lastUpdated
}) => {
  const formatLastUpdated = (date: Date | null | undefined): string => {
    if (!date) return '';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hours ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} days ago`;
  };

  return (
    <Box 
      sx={{ 
        mb: 3, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2
      }}
    >
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            color: 'white', 
            fontWeight: 600,
            mb: subtitle || lastUpdated ? 0.5 : 0
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
        {lastUpdated && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Last updated: {formatLastUpdated(lastUpdated)}
          </Typography>
        )}
      </Box>
      
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        {onRefresh && (
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={onRefresh}
            disabled={loading}
            sx={{
              borderColor: 'grey.700',
              color: 'grey.300',
              '&:hover': {
                borderColor: 'grey.600',
                bgcolor: 'grey.800'
              }
            }}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        )}
        {actions}
      </Box>
    </Box>
  );
};

export default PageHeader;
